import React, { useState, useEffect } from 'react';

interface Note {
  id: string;
  title: string;
  content: string;
  created_at: string;
  updated_at: string;
}

interface NotesSectionProps {
  transcriptId: string;
}

const NotesSection: React.FC<NotesSectionProps> = ({ transcriptId }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [newNoteTitle, setNewNoteTitle] = useState('');
  const [newNoteContent, setNewNoteContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (transcriptId) {
      fetchNotes();
    }
  }, [transcriptId]);

  // Reset form state when transcript changes
  useEffect(() => {
    // Reset all form states when transcript changes
    setIsAddingNote(false);
    setEditingNoteId(null);
    setNewNoteTitle('');
    setNewNoteContent('');
    setSaving(false);
  }, [transcriptId]);

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/notes/list?transcriptId=${transcriptId}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json() as {
          success: boolean;
          notes?: Note[];
          error?: string;
        };
        if (data.success) {
          setNotes(data.notes || []);
        }
      }
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNoteTitle.trim() || !newNoteContent.trim()) {
      return;
    }

    try {
      setSaving(true);
      
      const response = await fetch('/api/notes/add', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transcriptId,
          title: newNoteTitle.trim(),
          content: newNoteContent.trim()
        })
      });
      
      if (response.ok) {
        const data = await response.json() as {
          success: boolean;
          note?: Note;
          error?: string;
        };
        
        if (data.success && data.note) {
          setNotes(prev => [data.note!, ...prev]);
          setNewNoteTitle('');
          setNewNoteContent('');
          setIsAddingNote(false);
        } else {
          alert(data.error || 'Failed to add note');
        }
      } else {
        const errorData = await response.json() as { error?: string };
        alert(errorData.error || 'Failed to add note');
      }
    } catch (error) {
      console.error('Failed to add note:', error);
      alert('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateNote = async (noteId: string) => {
    const note = notes.find(n => n.id === noteId);
    if (!note || !newNoteTitle.trim() || !newNoteContent.trim()) {
      return;
    }

    try {
      setSaving(true);
      const response = await fetch('/api/notes/update', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          noteId,
          title: newNoteTitle.trim(),
          content: newNoteContent.trim()
        })
      });

      if (response.ok) {
        const data = await response.json() as {
          success: boolean;
          note?: Note;
          error?: string;
        };
        if (data.success && data.note) {
          setNotes(prev => prev.map(n => n.id === noteId ? data.note! : n));
          setEditingNoteId(null);
          setNewNoteTitle('');
          setNewNoteContent('');
        }
      } else {
        const errorData = await response.json() as { error?: string };
        alert(errorData.error || 'Failed to update note');
      }
    } catch (error) {
      console.error('Failed to update note:', error);
      alert('Failed to update note');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Are you sure you want to delete this note?')) {
      return;
    }

    try {
      const response = await fetch(`/api/notes/delete?noteId=${noteId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json() as {
          success: boolean;
          error?: string;
        };
        if (data.success) {
          setNotes(prev => prev.filter(n => n.id !== noteId));
        }
      } else {
        const errorData = await response.json() as { error?: string };
        alert(errorData.error || 'Failed to delete note');
      }
    } catch (error) {
      console.error('Failed to delete note:', error);
      alert('Failed to delete note');
    }
  };

  const startEditing = (note: Note) => {
    setEditingNoteId(note.id);
    setNewNoteTitle(note.title);
    setNewNoteContent(note.content);
    setIsAddingNote(false);
  };

  const cancelEditing = () => {
    setEditingNoteId(null);
    setIsAddingNote(false);
    setNewNoteTitle('');
    setNewNoteContent('');
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch {
      return 'Unknown date';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-400"></div>
          <span className="ml-2 text-gray-600">Loading notes...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
          📝 Notes
          <span className="text-sm font-normal text-gray-500">
            ({notes.length}/3)
          </span>
        </h3>
        {notes.length < 3 && !isAddingNote && !editingNoteId && (
          <button
            onClick={() => setIsAddingNote(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Note
          </button>
        )}
      </div>

      {/* Add Note Form */}
      {isAddingNote && (
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Add New Note</h4>
          <div className="space-y-3">
            <div>
              <input
                type="text"
                placeholder="Note title..."
                value={newNoteTitle}
                onChange={(e) => setNewNoteTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                maxLength={100}
              />
            </div>
            <div>
              <textarea
                placeholder="Note content..."
                value={newNoteContent}
                onChange={(e) => setNewNoteContent(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                maxLength={1000}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">
                {newNoteContent.length}/1000 characters
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={cancelEditing}
                  disabled={saving}
                  className="px-3 py-1 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddNote}
                  disabled={saving || !newNoteTitle.trim() || !newNoteContent.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && (
                    <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white"></div>
                  )}
                  Save Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notes List */}
      {notes.length === 0 && !isAddingNote ? (
        <div className="text-center py-8 text-gray-500">
          <div className="text-4xl mb-3">📝</div>
          <p className="text-sm">No notes yet. Add your first note to get started.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {notes.map((note) => (
            <div key={note.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
              {editingNoteId === note.id ? (
                // Edit Mode
                <div className="space-y-3">
                  <div>
                    <input
                      type="text"
                      value={newNoteTitle}
                      onChange={(e) => setNewNoteTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <textarea
                      value={newNoteContent}
                      onChange={(e) => setNewNoteContent(e.target.value)}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      maxLength={1000}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {newNoteContent.length}/1000 characters
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={cancelEditing}
                        disabled={saving}
                        className="px-3 py-1 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleUpdateNote(note.id)}
                        disabled={saving || !newNoteTitle.trim() || !newNoteContent.trim()}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {saving && (
                          <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white"></div>
                        )}
                        Update
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium text-gray-900 line-clamp-1">{note.title}</h4>
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={() => startEditing(note)}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Edit note"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete note"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm whitespace-pre-wrap line-clamp-3 mb-3">
                    {note.content}
                  </p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Created: {formatDate(note.created_at)}</span>
                    {note.updated_at !== note.created_at && (
                      <span>Updated: {formatDate(note.updated_at)}</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {notes.length >= 3 && !editingNoteId && (
        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 text-amber-800 text-sm">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Maximum of 3 notes per transcript. Delete a note to add a new one.</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesSection; 